import SubscriptionForm from "@components/SubscriptionForm"
import localizeCurrency from "@helpers/localizeCurrency"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Plan, StripePrice } from "src/types"
import { twMerge } from "tailwind-merge"

function formatTimeLeft(endTime: number): string {
  const distance = endTime - Date.now()
  if (distance <= 0) return "00:00"
  const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((distance % (1000 * 60)) / 1000)
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
}

function OfferCountdown({ endTime }: { endTime: number }) {
  const { t } = useTranslation()
  const [timeLeft, setTimeLeft] = useState(() => formatTimeLeft(endTime))

  useEffect(() => {
    const timer = setInterval(() => {
      const formatted = formatTimeLeft(endTime)
      setTimeLeft(formatted)
      if (formatted === "00:00") clearInterval(timer)
    }, 1000)
    return () => clearInterval(timer)
  }, [endTime])

  if (timeLeft === "00:00") return null

  return (
    <div className="mt-3 sm:mt-4">
      <div className="bg-custom-teal/10 rounded-lg px-4 py-1.5 text-center sm:rounded-xl sm:px-5 sm:py-2">
        <p className="text-custom-teal-dark text-xs font-semibold sm:text-sm">
          {t("pages.checkout.plans.offerEnds", { countdown: timeLeft })}
        </p>
      </div>
    </div>
  )
}

interface Props {
  bestOffer?: boolean
  currency: string
  isSubmitting: boolean
  periodAmount: number
  periodUnit: string
  plan: Plan
  setIsSubmitting: (isSubmitting: boolean) => void
  stripeIntroPrice: StripePrice | undefined
  stripePrice: StripePrice | undefined
  endTime: number
  isSelected?: boolean
  onSelect?: () => void
}

export default function PlanCard({
  bestOffer,
  currency,
  isSubmitting,
  periodAmount,
  periodUnit,
  plan,
  setIsSubmitting,
  stripeIntroPrice,
  stripePrice,
  endTime,
  isSelected,
  onSelect,
}: Props) {
  const { t } = useTranslation()

  if (!stripePrice || !stripeIntroPrice) return null

  const hasCurrencyOption =
    !!stripeIntroPrice.currency_options?.[currency]?.unit_amount_decimal

  const price = parseFloat(
    hasCurrencyOption
      ? stripePrice.currency_options[currency].unit_amount_decimal
      : stripePrice?.unit_amount_decimal,
  )

  const introPrice = parseFloat(
    hasCurrencyOption
      ? stripeIntroPrice.currency_options[currency].unit_amount_decimal
      : stripeIntroPrice?.unit_amount_decimal,
  )

  const priceCurrency = hasCurrencyOption ? currency : stripePrice.currency

  if (!price) return null

  const pricePerPeriod = price / periodAmount
  const introPricePerPeriod = introPrice / periodAmount

  const getTotalDays = (): number => {
    switch (periodUnit) {
      case "week":
        return periodAmount * 7
      case "month":
        return periodAmount * 30
      default:
        return 365
    }
  }

  const totalDays = getTotalDays()
  const pricePerDay = introPricePerPeriod / totalDays

  const localizePrice = (price: number | undefined) => {
    if (!price) return ""

    return localizeCurrency({
      amountInCents: price,
      currency: priceCurrency,
    })
  }

  const getCurrencySymbol = () => {
    if (!priceCurrency) return ""
    return (
      new Intl.NumberFormat("en", {
        style: "currency",
        currency: priceCurrency,
      })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value ?? ""
    )
  }

  return (
    <div
      className={twMerge(
        "relative overflow-hidden rounded-3xl bg-white shadow-lg transition-all",
        isSelected && "ring-custom-teal ring-2",
        onSelect && "cursor-pointer hover:shadow-xl",
        onSelect && !isSelected && "opacity-40",
      )}
      onClick={onSelect}
    >
      {bestOffer && (
        <div className="bg-custom-teal relative overflow-hidden px-4 py-2 text-center sm:py-3">
          <div className="animate-shine absolute inset-0 bg-linear-to-r from-transparent via-white/20 to-transparent" />
          <p className="relative text-base font-bold tracking-wide text-white uppercase sm:text-lg md:text-xl">
            {t("pages.checkout.plans.mostPopular")}
          </p>
        </div>
      )}

      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2 sm:gap-4">
          <div className="flex items-start gap-2 sm:gap-4">
            {onSelect && (
              <div className="mt-1 sm:mt-2">
                <div
                  className={twMerge(
                    "h-6 w-6 rounded-full border-2 sm:h-7 sm:w-7",
                    isSelected
                      ? "border-custom-teal bg-custom-teal flex items-center justify-center"
                      : "border-gray-300",
                  )}
                >
                  {isSelected && (
                    <div className="h-3 w-3 rounded-full bg-white sm:h-3.5 sm:w-3.5" />
                  )}
                </div>
              </div>
            )}

            <div className="text-left">
              <h3 className="text-custom-charcoal mb-2 text-xl font-bold sm:mb-3 sm:text-2xl md:text-3xl">
                {periodUnit === "year"
                  ? t("pages.checkout.plans.twelveMonths")
                  : `${periodAmount}-${t(`pages.checkout.plans.${periodUnit}`, { count: periodAmount })}`}
              </h3>

              <div className="flex items-center gap-2 sm:gap-3">
                <p className="text-base text-gray-400 line-through sm:text-lg md:text-xl">
                  {localizePrice(pricePerPeriod)}
                </p>

                <p className="text-custom-charcoal text-lg font-bold sm:text-xl md:text-2xl">
                  {localizePrice(introPricePerPeriod)}
                </p>
              </div>
            </div>
          </div>

          <div className="shrink-0 text-right">
            <p className="text-custom-teal mb-0.5 text-3xl leading-none font-bold sm:mb-1 sm:text-4xl md:text-5xl">
              {getCurrencySymbol()}
              {(pricePerDay / 100).toFixed(2)}
            </p>
            <p className="text-xs text-gray-400 sm:text-sm md:text-base">
              {t("pages.checkout.plans.perDay")}
            </p>
          </div>
        </div>

        {(!onSelect || isSelected) && <OfferCountdown endTime={endTime} />}

        {!onSelect && (
          <div className="mt-6 space-y-4">
            <SubscriptionForm
              buttonClassName="custom-button-primary uppercase"
              buttonTitle={t("pages.checkout.plans.subscribeNow")}
              currency={priceCurrency}
              fullPrice={stripePrice}
              isSubmitting={isSubmitting}
              plan={plan}
              priceId={stripeIntroPrice.id}
              setIsSubmitting={setIsSubmitting}
            />

            <p className="text-center text-sm">
              {t("pages.checkout.plans.cancelAnytime")}
            </p>

            <p className="text-center text-sm font-bold">
              {t("pages.checkout.plans.moneyBackGuarantee")}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
